import React, { useContext, useState } from 'react';
import PropTypes from 'prop-types';
import { Grid, FormControlLabel, InputAdornment } from '@mui/material';
import { TextField, IconButton, Autocomplete } from '@totalsoft/rocket-ui';
import { DateTime } from '@ts-private/rocket-extensions';
import Switch from '@mui/material/Switch';
import PersonSearch from '@mui/icons-material/PersonSearch';
import PersonSearchOutlined from '@mui/icons-material/PersonSearchOutlined';
import PJContainerWrapper from 'components/common/select/BrowserContainer/legalPartners/PJContainerWrapper';
import memoize from 'lodash.memoize';
import { onCheckBoxChange, addPropertyPrefix } from 'utils/propertyChangeAdapters';
import { RightsContext } from 'providers/RightsContext';
import { GlobalRightsContext } from 'providers/GlobalRightsContext';
import { useTranslation } from 'react-i18next';

const addGeneralDataPrefix = memoize(addPropertyPrefix('generalData'));

export const OfferPartnerPJGeneralData = ({
  partnerGeneralData,
  validationInfo,
  checkMandatoryFields,
  onPropertyChange,
  onUniqueIdBlur,
  disabled,
  legalForms,
  isRestructuring,
  partnerTypeCode,
  openExternalSearchConfirmationModal,
  isExternalVerificationInProgress,
  isPJ,
  isAssociatedPartner,
  setIsPartnerFromModal,
  canSearchExternally,
  partnerTypeId,
  disabledSearchByUniqueId
}) => {
  const { t } = useTranslation();
  const { masterPartnerSearch } = useContext(GlobalRightsContext);
  const [isBrowserOpen, setIsBrowserOpen] = useState(false);
  const {
    readOnlyFull,
    clientExternalInterogationWrite,
    offerPartnerRemarksRead,
    offerPartnerExtendedInformationRead,
    offerPartnerBasicInformationRead,
    offerPartnerRemarksWrite,
    offerPartnerExtendedInformationWrite,
    offerPartnerBasicInformationWrite
  } = useContext(RightsContext);

  const getLegalPartnerInfoTooltip = () => {
    if (!(canSearchExternally.uniqueIdIsValid && canSearchExternally.uniqueIdIsCIF))
      return t('OfferEdit.Client.PJ.ExternalInfoDisabled.PersonalIdentifier');

    if (!canSearchExternally.partnerNameIsValid) return t('OfferEdit.Client.PJ.ExternalInfoDisabled.CompanyName');

    if (window.location.pathname.includes('partner-files/new')) return t('OfferEdit.Client.PJ.GetPartnerFileExternalInformation');

    return t('OfferEdit.Client.PJ.GetExternalInformation');
  };

  const CustomEndIcons = ({ onCustomClick, disabled }) => (
    <InputAdornment position='end'>
      {masterPartnerSearch && (
        <IconButton
          id={'offerPartnerFormCard_pj_getLegalPartnerInformationBtn'}
          tooltip={t('OfferEdit.Client.PJ.GetLegalPartner')}
          color='primary'
          size='tiny'
          variant='text'
          disabled={readOnlyFull || disabled}
          onClick={onCustomClick}
        >
          <PersonSearch />
        </IconButton>
      )}
      {clientExternalInterogationWrite && (
        <IconButton
          id={'offerPartnerFormCard_pj_getLegalPartnerInformationBtn'}
          tooltip={getLegalPartnerInfoTooltip()}
          color='primary'
          size='tiny'
          variant='text'
          disabled={
            readOnlyFull ||
            !!validationInfo.uniqueId ||
            !(canSearchExternally.uniqueIdIsValid && canSearchExternally.uniqueIdIsCIF && canSearchExternally.partnerNameIsValid) ||
            isExternalVerificationInProgress ||
            window.location.pathname.includes('partner-files/new')
          }
          onClick={openExternalSearchConfirmationModal}
        >
          <PersonSearchOutlined />
        </IconButton>
      )}
    </InputAdornment>
  );

  return (
    <>
      <Grid container spacing={2} alignItems='center' mb={4}>
        {offerPartnerBasicInformationRead && (
          <>
            <Grid item xs={12} sm={6} md={4}>
              <PJContainerWrapper
                disabled={
                  disabled || !offerPartnerBasicInformationWrite || (!isAssociatedPartner && isRestructuring) || disabledSearchByUniqueId
                }
                label={t(`OfferEdit.Client.${partnerTypeCode}.PersonalIdentifier`)}
                onTextFieldChange={addGeneralDataPrefix(onPropertyChange)('uniqueId')}
                iconDisabled={disabledSearchByUniqueId}
                iconHidden={false}
                value={partnerGeneralData.uniqueId}
                onBlur={() => onUniqueIdBlur(partnerGeneralData.uniqueId)}
                error={!isRestructuring && (!!validationInfo.uniqueId || !canSearchExternally.uniqueIdIsValid)}
                helperText={
                  !isRestructuring &&
                  (validationInfo.uniqueId ||
                    (!canSearchExternally.uniqueIdIsValid && t(`OfferEdit.Client.${partnerTypeCode}.ExternalSearch.UniqueIdRequired`)))
                }
                hasCustomIcons
                isBrowserOpen={isBrowserOpen}
                setIsBrowserOpen={setIsBrowserOpen}
                partnerTypeId={partnerTypeId}
                customEndIcons={
                  <CustomEndIcons
                    onCustomClick={() => {
                      setIsBrowserOpen(true);
                      setIsPartnerFromModal(true);
                    }}
                    disabled={disabledSearchByUniqueId}
                  />
                }
              />
            </Grid>
            <Grid item xs={12} sm={6} md={4}>
              <TextField
                id={'offerPartnerFormCard_pj_partnerName'}
                fullWidth
                required={checkMandatoryFields}
                value={partnerGeneralData.partnerName || ''}
                error={!!validationInfo.partnerName || !canSearchExternally.partnerNameIsValid}
                helperText={
                  validationInfo.partnerName ||
                  (!canSearchExternally.partnerNameIsValid && t(`OfferEdit.Client.${partnerTypeCode}.ExternalSearch.PartnerNameRequired`))
                }
                name='partnerName'
                label={t('OfferEdit.Client.PJ.CompanyName')}
                onChange={addGeneralDataPrefix(onPropertyChange)('partnerName')}
                disabled={disabled || !offerPartnerBasicInformationWrite}
              />
            </Grid>
            <Grid item xs={12} sm={6} md={4}>
              <Autocomplete
                id={'offerPartnerFormCard_pj_legalForm'}
                fullWidth
                disabled={disabled || !offerPartnerBasicInformationWrite}
                required={checkMandatoryFields}
                value={partnerGeneralData.legalFormId}
                valueKey='legalFormId'
                labelKey='legalFormName'
                label={t('OfferEdit.Client.PJ.LegalForm')}
                options={legalForms}
                onChange={addGeneralDataPrefix(onPropertyChange)('legalFormId')}
                simpleValue
                error={validationInfo.legalFormId ? true : false}
                helperText={validationInfo.legalFormId ? t('Validations.Generic.Mandatory') : ''}
                isClearable
              />
            </Grid>
          </>
        )}
        {offerPartnerExtendedInformationRead && (
          <>
            <Grid item xs={12} sm={6} md={4}>
              <TextField
                id={'offerPartnerFormCard_pj_comercialRegistration'}
                fullWidth
                required={isPJ && checkMandatoryFields}
                value={partnerGeneralData.comercialRegistration || ''}
                error={validationInfo.comercialRegistration ? true : false}
                helperText={validationInfo.comercialRegistration}
                name='comercialRegistration'
                label={t('OfferEdit.Client.PJ.ComercialRegistration')}
                onChange={addGeneralDataPrefix(onPropertyChange)('comercialRegistration')}
                disabled={disabled || !offerPartnerExtendedInformationWrite}
              />
            </Grid>
            <Grid item xs={12} sm={6} md={4}>
              <DateTime
                id={'offerPartnerFormCard_pj_registrationDate'}
                fullWidth
                value={partnerGeneralData.registrationDate}
                error={validationInfo.registrationDate ? true : false}
                helperText={validationInfo.registrationDate}
                label={t('OfferEdit.Client.PJ.RegistrationDate')}
                onChange={addGeneralDataPrefix(onPropertyChange)('registrationDate')}
                timeFormat={false}
                disabled={disabled || !offerPartnerExtendedInformationWrite}
                required={checkMandatoryFields}
              />
            </Grid>
            <Grid item xs={12} sm={6} md={4}>
              <FormControlLabel
                control={
                  <Switch
                    id={'offerPartnerFormCard_pj_isExternal'}
                    checked={partnerGeneralData.isExternal || false}
                    onChange={onCheckBoxChange(addGeneralDataPrefix(onPropertyChange)('isExternal'))}
                    name='isExternal'
                    disabled={disabled || !offerPartnerExtendedInformationWrite}
                  />
                }
                label={t('OfferEdit.Client.PJ.External')}
              />
            </Grid>
          </>
        )}
        {offerPartnerRemarksRead && (
          <Grid item xs={12}>
            <TextField
              fullWidth
              id={'offerPartnerFormCard_pj_remarks'}
              label={t('OfferEdit.Client.Remarks')}
              value={partnerGeneralData.remark || ''}
              onChange={addGeneralDataPrefix(onPropertyChange)('remark')}
              multiline
              inputProps={{ maxLength: 255 }}
              variant='outlined'
              disabled={disabled || !offerPartnerRemarksWrite}
            />
          </Grid>
        )}
      </Grid>
    </>
  );
};

OfferPartnerPJGeneralData.propTypes = {
  partnerGeneralData: PropTypes.object.isRequired,
  validationInfo: PropTypes.object.isRequired,
  checkMandatoryFields: PropTypes.bool.isRequired,
  onPropertyChange: PropTypes.func.isRequired,
  onUniqueIdBlur: PropTypes.func.isRequired,
  legalForms: PropTypes.array.isRequired,
  disabled: PropTypes.bool.isRequired,
  isRestructuring: PropTypes.bool.isRequired,
  isAssociatedPartner: PropTypes.bool,
  isPJ: PropTypes.bool,
  partnerTypeId: PropTypes.number,
  partnerTypeCode: PropTypes.string,
  openExternalSearchConfirmationModal: PropTypes.func.isRequired,
  setIsPartnerFromModal: PropTypes.func.isRequired,
  canSearchExternally: PropTypes.object.isRequired,
  onCustomClick: PropTypes.array,
  isExternalVerificationInProgress: PropTypes.bool,
  disabledSearchByUniqueId: PropTypes.bool
};

export default OfferPartnerPJGeneralData;
